/**
 * @vuelto-surface
 * mobile: omit
 * desktop: ok
 * strategy: web-first
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';

export type OnboardingRailStep = {
  key: string;
  label: string;
  /** done | on | upcoming */
  state: 'done' | 'on' | 'upcoming';
};

type Props = {
  title: string;
  subtitle: string;
  steps: readonly OnboardingRailStep[];
  footer?: string;
};

/**
 * Rail brand del wizard — espejo del proto `onboarding-wizard-v2.html` / login C.
 */
export function OnboardingBrandRail({ title, subtitle, steps, footer }: Props) {
  return (
    <View
      style={styles.root}
      accessibilityRole="text"
      accessibilityLabel={`${title}. ${subtitle}`}
      testID="onboarding.brand-rail"
    >
      <View style={styles.gradTop} pointerEvents="none" />
      <View style={styles.gradBottom} pointerEvents="none" />
      <View style={styles.top}>
        <View style={styles.brandRow}>
          <View style={styles.markBox} accessibilityElementsHidden>
            <Text style={styles.markLetter}>V</Text>
          </View>
          <Text style={styles.wordmark}>Vuelto</Text>
        </View>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.sub}>{subtitle}</Text>
        <View style={styles.steps} accessibilityRole="list">
          {steps.map((step) => {
            const active = step.state === 'on';
            const done = step.state === 'done';
            return (
              <View
                key={step.key}
                style={[styles.stepRow, !active && !done && styles.stepRowMuted]}
                accessibilityRole="text"
                accessibilityState={{ selected: active }}
              >
                <View style={[styles.stepIcon, (active || done) && styles.stepIconOn]}>
                  <Text style={styles.stepIconText}>{done ? '✓' : step.key}</Text>
                </View>
                <Text style={[styles.stepLabel, active && styles.stepLabelOn]}>{step.label}</Text>
              </View>
            );
          })}
        </View>
      </View>
      {footer ? <Text style={styles.footer}>{footer}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    /** Proto: rail fijo 340px — el pane del form lleva el protagonismo. */
    width: 340,
    minWidth: 300,
    backgroundColor: colors.accent,
    paddingVertical: 36,
    paddingHorizontal: 32,
    justifyContent: 'space-between',
    gap: spacing.xl,
    overflow: 'hidden',
    position: 'relative',
  },
  gradTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '42%',
    backgroundColor: colors.accentStrong,
    opacity: 0.55,
  },
  gradBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '38%',
    backgroundColor: colors.accentMuted,
    opacity: 0.55,
  },
  top: {
    gap: 10,
    zIndex: 1,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  markBox: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  markLetter: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  wordmark: {
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: -0.3,
    color: '#FFFFFF',
  },
  title: {
    ...typography.titleXL,
    fontSize: 28,
    lineHeight: 34,
    letterSpacing: -0.8,
    color: '#FFFFFF',
    fontWeight: '800',
    marginTop: 12,
  },
  sub: {
    ...typography.body,
    fontSize: 15,
    lineHeight: 22,
    color: 'rgba(255,255,255,0.9)',
  },
  steps: {
    marginTop: 28,
    gap: 14,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  stepRowMuted: {
    opacity: 0.5,
  },
  stepIcon: {
    width: 22,
    height: 22,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  stepIconOn: {
    backgroundColor: 'rgba(255,255,255,0.28)',
  },
  stepIconText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
  },
  stepLabel: {
    flex: 1,
    fontSize: 14,
    lineHeight: 19,
    color: 'rgba(255,255,255,0.95)',
    fontWeight: '500',
  },
  stepLabelOn: {
    fontWeight: '700',
  },
  footer: {
    fontSize: 14,
    lineHeight: 20,
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '500',
    zIndex: 1,
  },
});
